/**
 * Client-side pagination for the availability-sorted collection grid.
 *
 * The collection template renders the FULL collection (up to Shopify's 250-item
 * Liquid limit) already globally sorted: available products first, sold-out
 * products last. This script slices that list into pages so that:
 *   - all products are reachable (no products dropped),
 *   - sold-out products always appear after every available product,
 *   - no empty pages are generated,
 *   - filtering/sorting (handled by the theme's AJAX) keeps working.
 *
 * Page state is kept in the URL hash (#page=N) so the server always renders the
 * full list (a server `?page=N` would make Shopify return an out-of-range,
 * empty page). Legacy `?page=N` links are transparently redirected to the hash.
 */
(function () {
  'use strict';

  var GRID_ID = 'CollectionProductGrid';
  var PAGE_DELTA = 2; // pages shown either side of the current page

  var Pagination = {
    init: function (reset) {
      var root = document.getElementById(GRID_ID);
      if (!root) return;

      this.root = root;
      this.list = root.querySelector('.productListing');
      this.wrapper = root.querySelector('[data-collection-pagination]');

      // Empty collection / empty filter result: nothing to paginate.
      if (!this.list || !this.wrapper) return;

      this.items = Array.prototype.slice.call(this.list.children).filter(function (el) {
        return el.classList && el.classList.contains('product');
      });

      this.perPage =
        parseInt(this.wrapper.getAttribute('data-per-page'), 10) ||
        parseInt(this.list.getAttribute('data-products-per-page'), 10) ||
        this.items.length ||
        1;
      this.total = this.items.length;
      this.totalPages = Math.max(1, Math.ceil(this.total / this.perPage));

      this.nav = this.wrapper.querySelector('[data-pagination-nav]');
      this.listEl = this.wrapper.querySelector('[data-pagination-list]');
      this.prevTpl = this.wrapper.querySelector('template[data-pagination-prev]');
      this.nextTpl = this.wrapper.querySelector('template[data-pagination-next]');
      this.startEl = this.wrapper.querySelector('[data-total-start]');
      this.endEl = this.wrapper.querySelector('[data-total-end]');
      this.progressEl = this.wrapper.querySelector('[data-total-progress]');

      this.bindGlobal();

      // Legacy ?page=N -> convert to hash so the server renders the full list.
      var legacy = this.getLegacyPageParam();
      if (legacy > 1) {
        this.redirectLegacyPage(legacy);
        return;
      }

      var page;
      if (reset) {
        // Filtering/sorting changed the result set: go back to the first page
        // and drop any stale #page= fragment.
        page = 1;
        if (/#page=\d+/.test(window.location.hash)) {
          history.replaceState(null, '', window.location.pathname + window.location.search);
        }
      } else {
        page = this.getPageFromHash();
      }

      this.showPage(page, false);
    },

    bindGlobal: function () {
      if (this._bound) return;
      this._bound = true;
      var self = this;

      document.addEventListener('click', function (event) {
        if (!self.wrapper) return;
        var link = event.target.closest('[data-page]');
        if (!link || !self.wrapper.contains(link)) return;
        event.preventDefault();
        var page = parseInt(link.getAttribute('data-page'), 10);
        if (!page) return;
        self.pushPage(page);
        self.showPage(page, true);
      });

      var onPop = function () {
        if (!self.items || !self.items.length) return;
        self.showPage(self.getPageFromHash(), false);
      };
      window.addEventListener('popstate', onPop);
      window.addEventListener('hashchange', onPop);
    },

    getLegacyPageParam: function () {
      try {
        var value = new URLSearchParams(window.location.search).get('page');
        return value ? parseInt(value, 10) || 0 : 0;
      } catch (e) {
        return 0;
      }
    },

    redirectLegacyPage: function (page) {
      var params;
      try {
        params = new URLSearchParams(window.location.search);
      } catch (e) {
        params = null;
      }
      var search = '';
      if (params) {
        params.delete('page');
        var qs = params.toString();
        search = qs ? '?' + qs : '';
      }
      window.location.replace(window.location.pathname + search + '#page=' + page);
    },

    getPageFromHash: function () {
      var match = window.location.hash.match(/page=(\d+)/);
      var page = match ? parseInt(match[1], 10) : 1;
      return this.clampPage(page);
    },

    clampPage: function (page) {
      page = parseInt(page, 10) || 1;
      if (page < 1) page = 1;
      if (page > this.totalPages) page = this.totalPages;
      return page;
    },

    pushPage: function (page) {
      var base = window.location.pathname + window.location.search;
      var url = page > 1 ? base + '#page=' + page : base;
      history.pushState({ scoderPage: page }, '', url);
    },

    showPage: function (page, doScroll) {
      if (!this.items.length) return;
      page = this.clampPage(page);
      this.currentPage = page;

      var start = (page - 1) * this.perPage;
      var end = start + this.perPage;

      for (var i = 0; i < this.items.length; i++) {
        this.items[i].style.display = i >= start && i < end ? '' : 'none';
      }

      this.renderControls(page);
      this.updateProgress(start, end);
      this.refreshLazyImages();

      if (doScroll && this.root) {
        var top = this.root.getBoundingClientRect().top + window.pageYOffset - 100;
        window.scrollTo({ top: top, behavior: 'smooth' });
      }
    },

    updateProgress: function (start, end) {
      var shownEnd = Math.min(end, this.total);
      if (this.startEl) this.startEl.textContent = this.total === 0 ? 0 : start + 1;
      if (this.endEl) this.endEl.textContent = shownEnd;
      if (this.progressEl) {
        var pct = this.total ? Math.round((shownEnd / this.total) * 100) : 100;
        this.progressEl.style.width = pct + '%';
      }
    },

    renderControls: function (page) {
      if (!this.listEl) return;

      if (this.totalPages <= 1) {
        if (this.nav) this.nav.hidden = true;
        this.listEl.innerHTML = '';
        return;
      }
      if (this.nav) this.nav.hidden = false;
      this.listEl.innerHTML = '';

      if (page > 1) {
        this.listEl.appendChild(this.makeArrow(this.prevTpl, page - 1));
      }

      var entries = this.buildPageList(page);
      for (var i = 0; i < entries.length; i++) {
        this.listEl.appendChild(this.makeNumber(entries[i], page));
      }

      if (page < this.totalPages) {
        this.listEl.appendChild(this.makeArrow(this.nextTpl, page + 1));
      }
    },

    makeArrow: function (tpl, targetPage) {
      var li;
      if (tpl && tpl.content && tpl.content.firstElementChild) {
        li = tpl.content.firstElementChild.cloneNode(true);
      } else {
        li = document.createElement('li');
        li.className = 'pagination-arrow';
        var fallback = document.createElement('a');
        fallback.className = 'pagination__item link';
        fallback.textContent = targetPage;
        li.appendChild(fallback);
      }
      var anchor = li.querySelector('a');
      if (anchor) {
        anchor.setAttribute('href', '#page=' + targetPage);
        anchor.setAttribute('data-page', targetPage);
      }
      return li;
    },

    makeNumber: function (entry, current) {
      var li = document.createElement('li');
      li.className = 'pagination-num';

      if (entry === '…') {
        li.innerHTML = '<span class="pagination__item">…</span>';
        return li;
      }

      if (entry === current) {
        var span = document.createElement('span');
        span.className = 'pagination__item pagination__item--current';
        span.setAttribute('aria-current', 'page');
        span.textContent = entry;
        li.appendChild(span);
      } else {
        var anchor = document.createElement('a');
        anchor.className = 'pagination__item link';
        anchor.setAttribute('href', '#page=' + entry);
        anchor.setAttribute('data-page', entry);
        anchor.setAttribute('aria-label', entry);
        anchor.textContent = entry;
        li.appendChild(anchor);
      }
      return li;
    },

    buildPageList: function (current) {
      var total = this.totalPages;
      var entries = [];
      var left = Math.max(2, current - PAGE_DELTA);
      var right = Math.min(total - 1, current + PAGE_DELTA);

      entries.push(1);
      if (left > 2) entries.push('…');
      for (var i = left; i <= right; i++) entries.push(i);
      if (right < total - 1) entries.push('…');
      if (total > 1) entries.push(total);

      return entries;
    },

    refreshLazyImages: function () {
      // Newly shown items were display:none, so lazy-loaders skipped them.
      // Nudge the theme's lazy-loader (lazysizes) to pick them up.
      if (window.lazySizes && window.lazySizes.loader && typeof window.lazySizes.loader.checkElems === 'function') {
        window.lazySizes.loader.checkElems();
      } else {
        window.dispatchEvent(new Event('scroll'));
        window.dispatchEvent(new Event('resize'));
      }
    }
  };

  window.ScoderCollectionPagination = Pagination;

  function boot() {
    Pagination.init(false);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
